import React, { useState, useEffect } from 'react';
import { MoreVertical, AlertCircle } from 'lucide-react';
import CardDetailModal from './CardDetailModal';
import { useTheme } from '../contexts/ThemeContext';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SortableCard = ({ card, onClick, getColorClass, boardType, isDark, isOverdue }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: card.card_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      data-testid={`card-${card.card_id}`}
      className={`${isDark ? 'bg-[#111]' : 'bg-white'} border-l-4 ${isOverdue ? 'border-l-red-500 bg-red-500/5' : getColorClass(card.color)} p-4 rounded-sm cursor-grab active:cursor-grabbing ${isDark ? 'hover:shadow-[0_0_10px_rgba(162,255,0,0.1)]' : 'hover:shadow-md'} transition-shadow ${isOverdue ? 'ring-1 ring-red-500/30' : ''}`}
    >
      <div className="flex items-start justify-between mb-2">
        <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'} text-sm`}>{card.title}</h4>
        <div className="flex items-center gap-1">
          {isOverdue && (
            <span className="payment-overdue-pulse">
              <AlertCircle className="w-4 h-4 text-red-500" />
            </span>
          )}
          <MoreVertical className="w-4 h-4 text-gray-500" />
        </div>
      </div>
      {isOverdue && (
        <div className="flex items-center gap-1 mb-2 payment-status-blink">
          <span className="text-xs font-medium text-red-400 bg-red-500/20 px-2 py-0.5 rounded">
            Payment Overdue
          </span>
        </div>
      )}
      {card.description && (
        <p className="text-xs text-gray-400 mb-2 line-clamp-2">{card.description}</p>
      )}
      {boardType === 'member_tracking' && card.billing_amount_gbp && (
        <div className={`text-xs ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} font-mono`}>
          £{card.billing_amount_gbp} / {card.billing_type}
        </div>
      )}
      {card.tags && card.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {card.tags.slice(0, 3).map((tag, i) => (
            <span key={i} className={`text-xs ${isDark ? 'bg-white/5 text-gray-400' : 'bg-gray-100 text-gray-600'} px-2 py-1 rounded`}>
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Droppable column wrapper
const DroppableColumn = ({ column, children, isOver, isDark }) => {
  const { setNodeRef } = useDroppable({
    id: `column-${column.column_id}`,
  });

  return (
    <div
      ref={setNodeRef}
      className={`p-4 space-y-3 min-h-[200px] max-h-[calc(100vh-300px)] overflow-y-auto transition-colors ${
        isOver ? `${isDark ? 'bg-[#A2FF00]/10' : 'bg-green-50'} border-2 border-dashed ${isDark ? 'border-[#A2FF00]/50' : 'border-green-400'}` : ''
      }`}
    >
      {children}
    </div>
  );
};

const KanbanBoard = ({ columns, streams, cards, onCardMove, onRefresh, boardType, initialCardId, onCardOpened, showRetired, onCardSelect, externalModalControl }) => {
  const { isDark } = useTheme();
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedColumnName, setSelectedColumnName] = useState(null);
  const [activeCard, setActiveCard] = useState(null);
  const [overId, setOverId] = useState(null);
  const [overdueCardIds, setOverdueCardIds] = useState([]);

  // Fetch overdue card IDs for payment alerts
  useEffect(() => {
    const fetchOverdueCards = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/payments/overdue-cards`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setOverdueCardIds(data.overdue_card_ids || []);
        }
      } catch (err) {
        console.error('Failed to fetch overdue cards:', err);
      }
    };

    fetchOverdueCards();
    // Refresh every 5 minutes
    const interval = setInterval(fetchOverdueCards, 300000);
    return () => clearInterval(interval);
  }, []);

  // Auto-open card if initialCardId is provided
  useEffect(() => {
    if (initialCardId && cards.length > 0) {
      const cardToOpen = cards.find(c => c.card_id === initialCardId);
      if (cardToOpen) {
        const column = columns.find(col => col.column_id === cardToOpen.column_id);
        setSelectedCard(cardToOpen);
        setSelectedColumnName(column?.name || null);
        if (onCardOpened) {
          onCardOpened();
        }
      }
    }
  }, [initialCardId, cards, columns, onCardOpened]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const getCardsByColumn = (columnId) => {
    return cards.filter(card => card.column_id === columnId);
  };

  const getColorClass = (color) => {
    switch (color) {
      case 'warning':
        return 'border-l-yellow-500';
      case 'critical':
        return 'border-l-red-500';
      default:
        return 'border-l-[#A2FF00]';
    }
  };

  const handleCardClick = (card, columnName) => {
    if (!activeCard) {
      // If parent wants to control modal externally, call onCardSelect instead
      if (externalModalControl && onCardSelect) {
        onCardSelect(card, columnName);
      } else {
        setSelectedCard(card);
        setSelectedColumnName(columnName);
      }
    }
  };

  const handleCloseModal = () => {
    setSelectedCard(null);
    setSelectedColumnName(null);
  };

  // Handle card update - refresh data and keep modal open with updated card
  const handleCardUpdate = async () => {
    if (onRefresh) {
      await onRefresh();
      // After refresh, update selectedCard with fresh data from the new cards
      // This is handled by the parent re-rendering with new cards prop
    }
  };

  // Update selectedCard when cards change (e.g., after a refresh)
  const selectedCardId = selectedCard?.card_id;
  const updatedSelectedCard = selectedCardId ? cards.find(c => c.card_id === selectedCardId) : null;
  
  // Use the updated card data if available, otherwise use the current selectedCard
  const cardForModal = updatedSelectedCard || selectedCard;

  const handleDragStart = (event) => {
    const { active } = event;
    const card = cards.find(c => c.card_id === active.id);
    setActiveCard(card);
  };

  const handleDragOver = (event) => {
    const { over } = event;
    setOverId(over?.id || null);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveCard(null);
    setOverId(null);

    if (!over) return;

    const draggedCard = cards.find(c => c.card_id === active.id);
    if (!draggedCard) return;

    // Determine target column ID
    let targetColumnId = null;
    
    if (over.id.toString().startsWith('column-')) {
      // Dropped directly on a column
      targetColumnId = over.id.toString().replace('column-', '');
    } else {
      // Dropped on another card - get that card's column
      const overCard = cards.find(c => c.card_id === over.id);
      if (overCard) {
        targetColumnId = overCard.column_id;
      }
    }

    // Only move if dropping to a different column
    if (targetColumnId && draggedCard.column_id !== targetColumnId) {
      await onCardMove(active.id, targetColumnId);
    }
  };

  const handleDragCancel = () => {
    setActiveCard(null);
    setOverId(null);
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
          {columns.map(column => {
            const columnCards = getCardsByColumn(column.column_id);
            const isColumnOver = overId === `column-${column.column_id}`;
            
            return (
              <div
                key={column.column_id}
                className={`flex-shrink-0 w-80 ${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm`}
                data-testid={`column-${column.name}`}
              >
                <div className={`p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between">
                    <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{column.name}</h3>
                    <span className="text-xs text-gray-500 font-mono">
                      {columnCards.length}
                    </span>
                  </div>
                </div>

                <SortableContext
                  id={`column-${column.column_id}`}
                  items={columnCards.map(c => c.card_id)}
                  strategy={verticalListSortingStrategy}
                >
                  <DroppableColumn column={column} isOver={isColumnOver} isDark={isDark}>
                    {columnCards.length === 0 ? (
                      <div className="text-center text-gray-500 text-sm py-8">
                        Drop cards here
                      </div>
                    ) : (
                      columnCards.map(card => (
                        <SortableCard
                          key={card.card_id}
                          card={card}
                          onClick={() => handleCardClick(card, column.name)}
                          getColorClass={getColorClass}
                          boardType={boardType}
                          isDark={isDark}
                          isOverdue={overdueCardIds.includes(card.card_id)}
                        />
                      ))
                    )}
                  </DroppableColumn>
                </SortableContext>
              </div>
            );
          })}
        </div>

        <DragOverlay>
          {activeCard ? (
            <div className={`${isDark ? 'bg-[#111]' : 'bg-white'} border-l-4 ${getColorClass(activeCard.color)} p-4 rounded-sm w-80 opacity-90 shadow-lg`}>
              <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'} text-sm`}>{activeCard.title}</h4>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Only render internal modal if not using external modal control */}
      {!externalModalControl && (
        <CardDetailModal
          card={cardForModal}
          isOpen={!!selectedCard}
          onClose={handleCloseModal}
          onUpdate={handleCardUpdate}
          boardType={boardType}
          columnName={selectedColumnName}
          defaultTab={boardType === 'media_production' ? 'media' : 'details'}
        />
      )}
    </>
  );
};

export default KanbanBoard;
